import { useState, useEffect, useCallback } from 'react';
import { IProperty } from '@/types';
import { getPropertiesRequest } from '@/api';
import { PropertiesSearchParams } from '@/types/api-types';
import { OpenStreetMapProvider, SearchResult } from 'leaflet-geosearch';
import convertAddressToStringForGeocoding from '@/utils/convertAddressToStringForGeocoding';

const geocodingProvider = new OpenStreetMapProvider();

// Інтерфейс фільтрів що приходять з компонента
// Зробимо його більш гнучким, щоб він міг приймати будь-які динамічні фільтри
interface PropertiesFilters {
  [key: string]: any; // Дозволяємо будь-які ключі
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

// Що повертає хук
interface UsePropertiesReturn {
  ownedProperties: IProperty[];
  isLoading: boolean;
  isFetching: boolean;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  refetch: () => void;
  error?: string;
}

// Допоміжна функція для геокодування однієї властивості
const geocodePropertyAddress = async (property: IProperty): Promise<IProperty> => {
  if (property.address && (!property.address.latitude || !property.address.longitude)) {
    const addressString = convertAddressToStringForGeocoding(property.address);
    if (addressString) {
      try {
        const results: SearchResult[] = await geocodingProvider.search({ query: addressString });
        if (results.length > 0) {
          const { y: latitude, x: longitude } = results[0];
          return {
            ...property,
            address: {
              ...property.address,
              latitude,
              longitude,
            },
          };
        }
      } catch (error) {
        // console.error(`Error geocoding address for property ${property.name} (${addressString}):`, error);
      }
    }
  }
  return property;
};

const useProperties = (filters?: PropertiesFilters): UsePropertiesReturn => {
  const [ownedProperties, setOwnedProperties] = useState<IProperty[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Конвертація назв колонок
  const mapSortColumn = (column?: string): PropertiesSearchParams['sortBy'] => {
    switch (column) {
      case 'property': return 'name';
      case 'address': return 'address';
      case 'units': return 'units';
      case 'leased': return 'leased';
      case 'vacant': return 'vacant';
      default: return undefined;
    }
  };

  // Конвертація фільтрів у параметри API
  const buildApiParams = (): PropertiesSearchParams => {
    const params: PropertiesSearchParams = {};

    if (!filters) return params;

    // Динамічно додаємо всі фільтри з об'єкта filters
    for (const key in filters) {
      if (Object.prototype.hasOwnProperty.call(filters, key)) {
        const value = filters[key];
        
        // Пропускаємо службові параметри, які обробляються окремо або не є фільтрами
        if (['page', 'limit', 'sortColumn', 'sortDirection'].includes(key)) {
          continue;
        }
        
        // Додаємо фільтр, якщо він має значення і не є порожнім рядком
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value;
        }
      }
    }

    // Обробка пагінації
    if (filters.page && filters.page > 0) {
      params.page = filters.page;
    }
    if (filters.limit && filters.limit > 0) {
      params.limit = filters.limit;
    }

    // Обробка сортування
    if (filters.sortColumn) {
      params.sortBy = mapSortColumn(filters.sortColumn);
    }
    if (filters.sortDirection) {
      params.sortDirection = filters.sortDirection;
    }

    // console.log('API Params being sent:', params); // Добавляем console.log здесь
    return params;
  };

  // Завантаження даних
  const fetchProperties = useCallback(async () => {
    setIsFetching(true);
    setError(undefined);

    try {
      const apiParams = buildApiParams();
      const response = await getPropertiesRequest(apiParams);

      let fetchedProperties: IProperty[] = [];
      let meta = { total: 0, totalPages: 0, hasNext: false, hasPrev: false };

      if (response.data.data && response.data.meta) {
        fetchedProperties = response.data.data;
        meta = response.data.meta;
      }

      // Геокодування властивостей, які не мають координат
      const geocodedPropertiesPromises = fetchedProperties.map(async (property, index) => {
        // Додаємо невелику затримку між запитами до геокодера, щоб уникнути блокування
        await new Promise(resolve => setTimeout(resolve, index * 100)); // 100ms затримка
        return geocodePropertyAddress(property);
      });

      const geocodedProperties = await Promise.all(geocodedPropertiesPromises);

      setOwnedProperties(geocodedProperties);
      setTotalItems(meta.total);
      setTotalPages(meta.totalPages);
      setHasNext(meta.hasNext);
      setHasPrev(meta.hasPrev);

    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch properties';
      setError(errorMessage);
      // console.error('Error fetching properties:', err);

      // Очищуємо дані при помилці
      setOwnedProperties([]);
      setTotalItems(0);
      setTotalPages(0);
      setHasNext(false);
      setHasPrev(false);

    } finally {
      setIsFetching(false);
      setIsLoading(false);
    }
  }, [filters]);

  // Ефект для першого завантаження
  useEffect(() => {
    setIsLoading(true);
    fetchProperties();
  }, [fetchProperties]);

  // Функція для примусового оновлення
  const refetch = useCallback(() => {
    setIsLoading(true);
    fetchProperties();
  }, [fetchProperties]);

  return {
    ownedProperties,
    isLoading,
    isFetching,
    totalItems,
    totalPages,
    hasNext,
    hasPrev,
    refetch,
    error,
  };
};

export default useProperties;
